import {
  GET_EXERCISES,
  GET_EXERCISES_SUCCESS,
  GET_EXERCISES_ERROR,
  CREATE_EXERCISE,
  CREATE_EXERCISE_SUCCESS,
  CREATE_EXERCISE_ERROR,
  UPDATE_EXERCISE,
  UPDATE_EXERCISE_SUCCESS,
  UPDATE_EXERCISE_ERROR,
  DELETE_EXERCISE,
  DELETE_EXERCISE_SUCCESS,
  DELETE_EXERCISE_ERROR,
  RESTORE_EXERCISE,
  RESTORE_EXERCISE_SUCCESS,
  RESTORE_EXERCISE_ERROR,
  GET_ARCHIVED_EXERCISES,
  GET_ARCHIVED_EXERCISES_SUCCESS,
  GET_ARCHIVED_EXERCISES_ERROR,
  SET_EDIT_EXERCISE,
  HANDLE_CREATE_OR_EDIT_EXERCISE,
  CLEAR_EDIT_EXERCISE,
  SET_IS_EXERCISE_EDIT,
  SAVE_EXERCISE_HISTORY,
  SAVE_EXERCISE_HISTORY_SUCCESS,
  SAVE_EXERCISE_HISTORY_ERROR,
  GET_EXERCISE_HISTORY_BY_IDS,
  GET_EXERCISE_HISTORY_BY_IDS_SUCCESS,
  GET_EXERCISE_HISTORY_BY_IDS_ERROR,
  GET_ALL_EXERCISE_HISTORY_BY_IDS_SUCCESS,
  GET_ALL_EXERCISE_HISTORY_BY_IDS_ERROR,
  GET_EXERCISES_HISTORY_BY_IDS_ARR,
  GET_EXERCISES_HISTORY_BY_IDS_ARR_SUCCESS,
  GET_EXERCISES_HISTORY_BY_IDS_ARR_ERROR,
  GET_EXERCISE_CATEGORIES,
  GET_EXERCISE_CATEGORIES_SUCCESS,
  GET_EXERCISE_CATEGORIES_ERROR,
  GET_MUSCLES,
  GET_MUSCLES_SUCCESS,
  GET_MUSCLES_ERROR,
  SET_SINGLE_USER_EXERCISE_HISTORY,
  GET_RECENT_PRS,
  GET_RECENT_PRS_SUCCESS,
  GET_RECENT_PRS_ERROR,
} from './constants';

const ALL_HISTORY_LRU_CAP = 25;

function appendBoundedHistory(map, exerciseId, payload) {
  if (!exerciseId) return map;
  // Re-key into a fresh object so the most-recent entry is last in
  // insertion order — that lets us drop the oldest when over the cap.
  const next = { ...map };
  delete next[exerciseId];
  next[exerciseId] = payload;
  const keys = Object.keys(next);
  if (keys.length <= ALL_HISTORY_LRU_CAP) return next;
  const trimmed = {};
  keys.slice(keys.length - ALL_HISTORY_LRU_CAP).forEach((k) => {
    trimmed[k] = next[k];
  });
  return trimmed;
}

const initialState = {
  exercises: [],
  // Caller's archived (soft-deleted) exercises — populated on demand by
  // the Archived view toggle, kept separate from the active `exercises`
  // list so the main library never shows hidden entries.
  archivedExercises: [],
  getExercisesLoading: false,
  getExercisesErr: null,
  createExerciseLoading: false,
  createExerciseErr: null,
  editExercise: {
    name: '',
    instruction: '',
    category: [],
    muscles: [],
    musclesSecondary: [],
    equipment: [],
    variations: [],
    videos: [],
    images: [],
  },
  createExerciseSuccess: false,
  isExerciseEdit: false,
  exerciseOptions: null,
  exerciseHistory: {},
  exerciseHistoryErr: null,
  exerciseHistoryLoading: { exerciseId: null, loading: false },
  highestLoadSet: null,
  exercisesHistory: {},
  exercisesHistoryErr: null,
  exercisesHistoryLoading: false,
  exercisesHistorySuccess: false,
  // Keyed by exerciseId so quick navigation between exercises doesn't
  // show stale data from the previously viewed exercise. Bounded via
  // LRU eviction (see ALL_HISTORY_LRU_CAP) — a user is unlikely to
  // re-visit dozens of exercises in a single session.
  allExerciseHistoryById: {},
  createExerciseHistoryLoading: false,
  exerciseCategories: null,
  muscles: [],
  singleUserExerciseHistory: null,
  recentPrs: [],
  recentPrsLoading: false,
  recentPrsErr: null,
};

function exercises(state = initialState, action) {
  const { type, payload } = action;
  switch (type) {
    case GET_EXERCISES:
      return {
        ...state,
        getExercisesErr: false,
        getExercisesLoading: true,
      };
    case GET_EXERCISES_SUCCESS:
      return {
        ...state,
        exercises: payload,
        getExercisesLoading: false,
      };
    case GET_EXERCISES_ERROR:
      return {
        ...state,
        getExercisesLoading: true,
        getExercisesErr: action.err,
      };
    case CREATE_EXERCISE:
      return {
        ...state,
        createExerciseLoading: true,
        createExerciseErr: null,
        createExerciseSuccess: false,
      };
    case CREATE_EXERCISE_SUCCESS:
      return {
        ...state,
        exercises: [...state.exercises, payload],
        createExerciseErr: null,
        createExerciseSuccess: true,
        createExerciseLoading: false,
      };
    case CREATE_EXERCISE_ERROR:
      return {
        ...state,
        createExerciseLoading: false,
        createExerciseErr: action.err,
        createExerciseSuccess: false,
      };
    case UPDATE_EXERCISE:
      return {
        ...state,
        createExerciseLoading: true,
        createExerciseErr: null,
        createExerciseSuccess: false,
      };
    case UPDATE_EXERCISE_SUCCESS:
      return {
        ...state,
        exercises: state.exercises.map((exercise) =>
          exercise._id === payload._id ? payload : exercise
        ),
        createExerciseErr: null,
        createExerciseSuccess: true,
        createExerciseLoading: false,
      };
    case UPDATE_EXERCISE_ERROR:
      return {
        ...state,
        createExerciseLoading: false,
        createExerciseErr: action.err,
        createExerciseSuccess: false,
      };
    case DELETE_EXERCISE:
      return {
        ...state,
        createExerciseLoading: true,
        createExerciseErr: null,
      };
    case DELETE_EXERCISE_SUCCESS:
      // payload is the deleted/archived exercise's _id — drop it from the
      // active list (whether it was hard-deleted or soft-archived, it's
      // no longer part of the caller's active library).
      return {
        ...state,
        exercises: state.exercises.filter(
          (exercise) => exercise._id !== payload
        ),
        createExerciseErr: null,
      };
    case DELETE_EXERCISE_ERROR:
      return {
        ...state,
        createExerciseLoading: false,
        createExerciseErr: action.err,
      };
    case RESTORE_EXERCISE:
      return {
        ...state,
        createExerciseErr: null,
      };
    case RESTORE_EXERCISE_SUCCESS:
      // payload is the restored exercise's _id — drop it from the archived
      // list. The saga also refetches the active list so it reappears there.
      return {
        ...state,
        archivedExercises: state.archivedExercises.filter(
          (exercise) => exercise._id !== payload
        ),
        createExerciseErr: null,
      };
    case RESTORE_EXERCISE_ERROR:
      return {
        ...state,
        createExerciseErr: action.err,
      };
    case GET_ARCHIVED_EXERCISES:
      return {
        ...state,
        getExercisesErr: false,
      };
    case GET_ARCHIVED_EXERCISES_SUCCESS:
      return {
        ...state,
        archivedExercises: payload,
      };
    case GET_ARCHIVED_EXERCISES_ERROR:
      return {
        ...state,
        archivedExercises: [],
        getExercisesErr: action.err,
      };
    case SET_EDIT_EXERCISE:
      return {
        ...state,
        editExercise: payload,
      };
    case HANDLE_CREATE_OR_EDIT_EXERCISE:
      return {
        ...state,
        editExercise: { ...state.editExercise, ...action.editExercise },
        createExerciseErr: action.err,
        exerciseOptions: action.exerciseOptions,
      };
    case CLEAR_EDIT_EXERCISE:
      return {
        ...state,
        editExercise: {
          name: '',
          instruction: '',
          category: [],
          muscles: [],
          musclesSecondary: [],
          equipment: [],
          variations: [],
          videos: [],
          images: [],
        },
      };
    case SET_IS_EXERCISE_EDIT:
      return {
        ...state,
        isExerciseEdit: action.value,
      };
    case SAVE_EXERCISE_HISTORY:
      return {
        ...state,
        exercisesHistorySuccess: false,
        createExerciseHistoryLoading: true,
      };
    case SAVE_EXERCISE_HISTORY_SUCCESS:
      return {
        ...state,
        exercisesHistorySuccess: true,
        createExerciseHistoryLoading: false,
      };
    case SAVE_EXERCISE_HISTORY_ERROR:
      return {
        ...state,
        exercisesHistorySuccess: false,
        createExerciseHistoryLoading: false,
      };
    case GET_EXERCISE_HISTORY_BY_IDS:
      return {
        ...state,
        exerciseHistory: {},
        exerciseHistoryErr: false,
        exerciseHistoryLoading: action.allHistory
          ? true
          : { exerciseId: action.exerciseId, loading: true },
      };
    case GET_EXERCISE_HISTORY_BY_IDS_SUCCESS: {
      const normalizedLastHistory = payload.lastHistory || payload;
      const exerciseId = payload.exerciseId;
      const normalizedEntry = payload.lastHistory
        ? payload
        : {
            ...payload,
            lastHistory: normalizedLastHistory,
          };
      return {
        ...state,
        exerciseHistory: normalizedLastHistory,
        exercisesHistory: exerciseId
          ? {
              ...state.exercisesHistory,
              [exerciseId]: {
                ...state.exercisesHistory?.[exerciseId],
                ...normalizedEntry,
              },
            }
          : state.exercisesHistory,
        exerciseHistoryLoading: { exerciseId: null, loading: false },
        highestLoadSet: payload.highestLoadSet || state.highestLoadSet,
      };
    }
    case GET_EXERCISE_HISTORY_BY_IDS_ERROR:
      return {
        ...state,
        exerciseHistory: {},
        exerciseHistoryLoading: false,
        exerciseHistoryErr: action.err,
        highestLoadSet: null,
      };
    case GET_ALL_EXERCISE_HISTORY_BY_IDS_SUCCESS: {
      // The saga threads the requested exerciseId through `action.exerciseId`
      // (the payload itself can carry a populated `exerciseId` object whose
      // `_id` is the same string — fall back to that for resilience).
      const id =
        action.exerciseId ||
        (payload && (payload.exerciseId?._id || payload.exerciseId)) ||
        null;
      return {
        ...state,
        exerciseHistoryLoading: false,
        allExerciseHistoryById: appendBoundedHistory(
          state.allExerciseHistoryById,
          id,
          payload
        ),
        singleUserExerciseHistory: payload,
      };
    }
    case SET_SINGLE_USER_EXERCISE_HISTORY:
      return {
        ...state,
        exerciseHistoryLoading: false,
        singleUserExerciseHistory: payload,
      };
    case GET_ALL_EXERCISE_HISTORY_BY_IDS_ERROR:
      return {
        ...state,
        // Don't wipe cached entries on error — the consumer can still
        // render stale data while showing the error state.
        exerciseHistoryLoading: false,
        exerciseHistoryErr: action.err,
      };
    case GET_EXERCISES_HISTORY_BY_IDS_ARR:
      return {
        ...state,
        exercisesHistoryErr: false,
        exercisesHistoryLoading: true,
      };
    case GET_EXERCISES_HISTORY_BY_IDS_ARR_SUCCESS: {
      // The endpoint returns two different shapes depending on the
      // query mode:
      //   - `?exerciseIds=all` → raw array of full ExerciseHistory docs
      //     (used by the user/trainer "all exercises" surfaces).
      //   - `?exerciseIds=id1,id2,...` → already-keyed object map
      //     `{ [exerciseId]: { lastHistory, highestLoadSet } }` (used
      //     by the workout modal for the per-exercise Last vol / PR
      //     display).
      //
      // Normalize both into a single keyed-by-exerciseId object so
      // downstream consumers don't trample each other: the previous
      // implementation overwrote whichever shape landed last, so if
      // the V2 client profile (which calls 'all') mounted after the
      // modal had populated specific IDs, `exercisesHistory[id]?.
      // lastHistory` would resolve against an array and return
      // `undefined` — that's why Last vol disappeared after a refresh.
      //
      // For the array case, denormalize `lastHistory` from
      // `history[history.length - 1]` into each entry so the modal's
      // existing `state[id].lastHistory.volume` lookup keeps working
      // alongside the full doc that the list/chart consumers need.
      // Those consumers now read via `Object.values(exercisesHistory)`.
      let normalized = payload;
      if (Array.isArray(payload)) {
        normalized = payload.reduce((acc, entry) => {
          const id = String(
            entry?.exerciseId?._id || entry?.exerciseId || ''
          );
          if (!id) return acc;
          const lastHistory =
            Array.isArray(entry.history) && entry.history.length
              ? entry.history[entry.history.length - 1]
              : null;
          acc[id] = { ...entry, lastHistory };
          return acc;
        }, {});
      }
      return {
        ...state,
        exercisesHistory: normalized,
        exercisesHistoryLoading: false,
      };
    }
    case GET_EXERCISES_HISTORY_BY_IDS_ARR_ERROR:
      return {
        ...state,
        exercisesHistory: {},
        exercisesHistoryLoading: false,
        exercisesHistoryErr: action.err,
      };
    case GET_EXERCISE_CATEGORIES:
      return {
        ...state,
        exerciseCategories: state.exerciseCategories,
      };
    case GET_EXERCISE_CATEGORIES_SUCCESS:
      return {
        ...state,
        exerciseCategories: action.payload,
      };
    case GET_EXERCISE_CATEGORIES_ERROR:
      return {
        ...state,
        exerciseCategories: null,
      };
    case GET_MUSCLES:
      return {
        ...state,
        muscles: state.muscles,
      };
    case GET_MUSCLES_SUCCESS:
      return {
        ...state,
        muscles: action.payload,
      };
    case GET_MUSCLES_ERROR:
      return {
        ...state,
        muscles: [],
      };
    case GET_RECENT_PRS:
      return {
        ...state,
        recentPrsLoading: true,
        recentPrsErr: null,
      };
    case GET_RECENT_PRS_SUCCESS:
      return {
        ...state,
        recentPrs: Array.isArray(payload) ? payload : [],
        recentPrsLoading: false,
        recentPrsErr: null,
      };
    case GET_RECENT_PRS_ERROR:
      return {
        ...state,
        recentPrsLoading: false,
        recentPrsErr: action.err,
      };
    default:
      return state;
  }
}

export default exercises;
